import { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "motion/react";

import useWizard from "./useWizard";
import { ACCENT_COLOR } from "./theme";

const STEP_ICONS = {
  1: "fa-circle-info",
  2: "fa-file-signature",
  3: "fa-clipboard-user",
  4: "fa-money-bill-wave",
  5: "fa-circle-check",
};

const navVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.07,
      delayChildren: 0.15,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: [0.25, 0.1, 0.25, 1] },
  },
};

export default function WizardNav() {
  const {
    steps,
    currentStep,
    currentSubStep,
    completedSteps,
    highestStepReached,
    committedProgramName,
    goToStep,
    isStepValid,
    isSubStepValid,
    data,
  } = useWizard();

  const titleRef = useRef(null);
  const [titleHeight, setTitleHeight] = useState("auto");
  const hasAnimated = useRef(false);

  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      const h = entry.contentRect.height;
      if (h > 0) setTitleHeight(h);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Nav
      as={motion.div}
      variants={navVariants}
      initial="hidden"
      animate="visible"
    >
      <NavCenter>
        <motion.div variants={itemVariants}>
          <NavTitleWrap
            animate={{ height: titleHeight }}
            transition={hasAnimated.current ? { duration: 0.3, ease: [0.4, 0, 0.2, 1] } : { duration: 0 }}
            onAnimationComplete={() => { hasAnimated.current = true; }}
          >
            <NavTitle ref={titleRef} className="merri">
              {committedProgramName || "RE Setup"}
            </NavTitle>
          </NavTitleWrap>
        </motion.div>
        <StepList>
          {steps.filter((s) => s.id !== 5).map((step) => {
            const isActive = step.id === currentStep;
            const isCompleted = completedSteps.includes(step.id);
            // Can go back to earlier/completed steps, but can't go forward past an invalid step
            const priorStepsValid = steps
              .filter((s) => s.id < step.id)
              .every((s) => isStepValid(s.id, data));
            const canClick =
              (isCompleted || step.id <= currentStep || step.id <= highestStepReached) && priorStepsValid;
            const hasSubSteps = step.subSteps.length > 0;
            const maxNav = step.lastMainSubStep ?? step.subSteps.length - 1;
            const targetSubStep = hasSubSteps
              ? (() => {
                  const first = step.subSteps.findIndex((_, idx) => idx <= maxNav && !isSubStepValid(step.id, idx, data));
                  return first !== -1 ? first : maxNav;
                })()
              : 0;

            return (
              <StepItem key={step.id} as={motion.li} variants={itemVariants}>
                <StepRow
                  $active={isActive}
                  $completed={isCompleted}
                  $clickable={canClick && !isActive}
                  $enabled={canClick}
                  onClick={() => canClick && !isActive && goToStep(step.id, targetSubStep)}
                >
                  <StepIcon $active={isActive} $completed={isCompleted} $enabled={canClick}>
                    <i className={`fas ${isCompleted ? "fa-check" : STEP_ICONS[step.id]}`} />
                  </StepIcon>
                  <StepLabel $active={isActive}>{step.label}</StepLabel>
                </StepRow>

                <AnimatePresence initial={false}>
                  {isActive && step.subSteps.length > 0 && (
                    <SubStepList
                      as={motion.ul}
                      key={`substeps-${step.id}`}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      {(step.navGroups || step.subSteps.slice(0, maxNav + 1).map((label, i) => ({ label, range: [i, i] }))).map((item, ni) => {
                        const isSubActive = currentSubStep >= item.range[0] && currentSubStep <= item.range[1];
                        const priorSubsValid = Array.from({ length: item.range[0] }, (_, i) => i)
                          .every((i) => isSubStepValid(step.id, i, data));
                        const subCanClick =
                          item.range[0] <= currentSubStep || priorSubsValid;
                        return (
                        <SubStepItem
                          key={ni}
                          $active={isSubActive}
                          $clickable={subCanClick}
                          onClick={() => subCanClick && goToStep(step.id, item.range[0])}
                        >
                          <SubStepIcon $active={isSubActive}>
                            <i className={isSubActive ? "fas fa-circle" : "far fa-circle"} />
                          </SubStepIcon>
                          {item.label}
                        </SubStepItem>
                        );
                      })}
                    </SubStepList>
                  )}
                </AnimatePresence>
              </StepItem>
            );
          })}
        </StepList>
      </NavCenter>
    </Nav>
  );
}

const Nav = styled.div`
  width: 280px;
  min-width: 280px;
  background: #f5f6f8;
  border-right: 1px solid #e5e7eb;
  padding: 32px 28px;
  display: flex;
  flex-direction: column;
  border-radius: 32px 0 0 32px;
`;

const NavCenter = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const NavTitleWrap = styled(motion.div)`
  overflow: hidden;
  margin-bottom: 28px;
`;

const NavTitle = styled.div`
  font-size: 26px;
  color: #182941;
  padding-left: 4px;
`;

const StepList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const StepItem = styled.li``;

const StepRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  border-radius: 8px;
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};
  background: transparent;
  transition: background 0.15s;
  opacity: ${(p) => (p.$enabled ? 1 : 0.45)};

  &:hover {
    background: ${(p) => (p.$clickable ? "rgba(0,0,0,0.04)" : "transparent")};
  }
`;

const StepIcon = styled.div`
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
  color: ${(p) =>
    p.$completed ? "#22c55e" : p.$active ? ACCENT_COLOR : "#696a6c"};
  transition: color 0.15s;
`;

const StepLabel = styled.div`
  font-size: 18px;
  line-height: 1.1;
  color: ${(p) => (p.$active ? ACCENT_COLOR : "#696a6c")};
`;

const SubStepList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  overflow: hidden;
`;

const SubStepItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  padding: 5px 8px;
  border-radius: 6px;
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};
  color: ${(p) => (p.$active ? ACCENT_COLOR : "#696a6c")};
  opacity: ${(p) => (p.$clickable ? 1 : 0.45)};
  background: transparent;
  transition: background 0.15s, opacity 0.15s;

  &:hover {
    color: ${(p) => (p.$clickable ? ACCENT_COLOR : undefined)};
    background: ${(p) => (p.$clickable ? "rgba(0, 0, 0, 0.04)" : "transparent")};
  }
`;


const SubStepIcon = styled.div`
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 8px;
  flex-shrink: 0;
  color: ${(p) => (p.$active ? ACCENT_COLOR : "#696a6c")};
  -webkit-text-stroke: ${(p) => (p.$active ? "0" : "1.5px")};
  transition: color 0.15s;
`;
